import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { usePageTitle } from '../../../hooks/usePageTitle';
import DashboardLayout from '../../../layouts/DashboardLayout/DashboardLayout';
import PageFooter from '../../../components/PageFooter/PageFooter';
import styles from '../legalPage.module.css';

function StarterPlan() {
  return (
    <>
      <p className={styles.effectiveDate}>Effective date: October 28, 2025.</p>
      <p className={styles.intro}>These Terms of Use ("Agreement") govern your participation in the POW Starter OTP Plan ("Program"), provided by Prime Bridge Securities LLC ("PB") through AkriTrade, Inc. ("POW"). By registering, purchasing access, or using a POW trading account, you agree to abide by these Terms in full.</p>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>1. ELIGIBILITY</h2>
        <p className={styles.body}>1.1 You must be at least 18 years old to participate in the Program.</p>
        <p className={styles.body}>1.2 You confirm that you are legally permitted to trade financial instruments in your country of residence.</p>
        <p className={styles.body}>1.3 Each account is individual and non-transferable.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>2. ACCOUNT SETUP</h2>
        <p className={styles.body}>2.1 Each simulated trading account is issued with a notional balance (e.g., $100,000).</p>
        <p className={styles.body}>2.2 Program fees are non-refundable once payment has been processed.</p>
        <p className={styles.body}>2.3 Multiple accounts are permitted but must comply with the diversification requirements outlined in Section 6.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>3. RISK RULES</h2>
        <p className={styles.body}>A breach of any of the following risk management rules will result in account termination. Once terminated, a trader must purchase a new account to resume participation.</p>

        <h3 className={styles.subHeading}>3.1 Daily loss limit</h3>
        <p className={styles.body}>3.1.1 Maximum daily loss: $3,000 (3% of initial balance).</p>
        <p className={styles.body}>3.1.2 The daily loss limit resets at 00:00 platform time (GMT+2).</p>

        <h3 className={styles.subHeading}>3.2 Maximum trailing loss</h3>
        <p className={styles.body}>3.2.1 The maximum trailing loss is 5% of the highest equity achieved since account creation.</p>
        <p className={styles.body}>3.2.2 After an approved withdrawal, the trailing loss resets based on post-withdrawal equity.</p>

        <h3 className={styles.subHeading}>3.3 Daily profit cap</h3>
        <p className={styles.body}>3.3.1 Maximum daily profit: $1,000 (1% of the starting day's balance).</p>
        <p className={styles.body}>3.3.2 The cap applies to both realized and unrealized profit and resets daily at 00:00 platform time (GMT+2).</p>
        <p className={styles.body}>3.3.3 Exceeding the daily profit cap:</p>
        <ul className={styles.list}>
          <li className={styles.listItem}>First offense: Account reset to the starting day balance.</li>
          <li className={styles.listItem}>Second offense: Account closure with forfeiture of profits and fees.</li>
        </ul>

        <h3 className={styles.subHeading}>3.4 Profit cap</h3>
        <p className={styles.body}>3.4.1 Maximum realized profit per trading cycle: $4,000 (4%). This profit cap applies strictly to the withdrawal event itself, regardless of how many days it takes the trader to accumulate the profit.</p>
        <p className={styles.body}>3.4.2 Traders are eligible to withdraw funds once the realized profit reaches the $4,000 cap.</p>
        <p className={styles.body}>3.4.3 Profits exceeding the $4,000 cap will not be credited or paid out. The moment a withdrawal is executed, trading is disabled and the account is placed on a 30 calendar days hold.</p>

        <h3 className={styles.subHeading}>3.5 Absolute profit cap</h3>
        <p className={styles.body}>3.5.1 The absolute maximum total withdrawal from the POW Starter OTP Plan is $8,000 per $100k account.</p>
        <p className={styles.body}>3.5.2 Upon reaching this limit, traders graduate from the Starter Program and receive a 20% discount on any standard POW OTP plan.</p>
        <p className={styles.body}>3.5.3 Graduated traders are no longer eligible to purchase new Starter OTP accounts.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>4. PAYOUTS</h2>
        <p className={styles.body}>4.1 The first payout is available 30 days after the first trade is placed.</p>
        <p className={styles.body}>4.2 Subsequent payouts are permitted every 28 days, subject to full compliance with all Program rules.</p>
        <p className={styles.body}>4.3 Payouts follow an 80/20 split – 80% to the trader and 20% retained by the liquidity provider.</p>
        <p className={styles.body}>4.4 Withdrawal requests that violate caps or anti-exploit rules will be denied.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>5. OFFENSES AND PENALTIES</h2>
        <p className={styles.body}>5.1 Daily Profit Cap breaches follow the penalties outlined in Section 3.3.</p>
        <p className={styles.body}>5.2 Any two major breaches (loss or profit cap violations) result in permanent account closure.</p>
        <p className={styles.body}>5.3 In all cases of rule violations, profits are forfeited and fees are non-refundable.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>6. MULTIPLE ACCOUNTS AND DIVERSIFICATION</h2>
        <p className={styles.body}>6.1 Traders operating multiple accounts must employ diversified trading strategies.</p>
        <p className={styles.body}>6.2 The following actions are strictly prohibited:</p>
        <ul className={styles.list}>
          <li className={styles.listItem}>Executing the same trades or symbols across multiple accounts.</li>
          <li className={styles.listItem}>Cross-hedging positions between accounts.</li>
          <li className={styles.listItem}>Mirroring trade strategies across accounts.</li>
        </ul>
        <p className={styles.body}>6.3 Penalty: All related accounts will be closed, profits withheld, and fees forfeited.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>7. TRADING RESTRICTIONS</h2>
        <p className={styles.body}>7.1 Use of arbitrage, latency exploitation, toxic order flow, or any trading activity deemed abusive by POW is prohibited.</p>
        <p className={styles.body}>7.2 All trading activity is monitored to ensure fair use and compliance.</p>
        <p className={styles.body}>7.3 Accounts found engaging in restricted practices are subject to termination without refund.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>8. PROGRAM DISCRETION</h2>
        <p className={styles.body}>8.1 PB and POW reserve the right to modify, suspend, or terminate any account found in violation of Program rules.</p>
        <p className={styles.body}>8.2 Rules and payout terms may be adjusted at any time to maintain fairness and sustainability.</p>
        <p className={styles.body}>8.3 POW reserves the right to deny payouts or access to the Program in cases of suspicious or abusive activity. In such cases, collected fees may be refunded at PB's or POW's discretion.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>9. LIMITATION OF LIABILITY</h2>
        <p className={styles.body}>9.1 PB and POW provide access to simulated or risk-managed trading accounts; no actual funds are deposited or traded on the trader's behalf.</p>
        <p className={styles.body}>9.2 PB and POW shall not be held liable for any trading losses, market risks, or technical malfunctions beyond their control.</p>
        <p className={styles.body}>9.3 Participation in the Program is entirely at the trader's own risk.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>10. GOVERNING LAW</h2>
        <p className={styles.body}>10.1 These Terms are governed by and construed in accordance with the laws of Saint Vincent and the Grenadines.</p>
        <p className={styles.body}>10.2 Any disputes shall be resolved exclusively in the courts of Saint Vincent and the Grenadines.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>11. ACCEPTANCE</h2>
        <p className={styles.body}>By registering for or participating in the POW Starter OTP Plan, you acknowledge that you have read, understood, and agreed to these Terms of Use in full.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>12. INACTIVITY RULE</h2>
        <p className={styles.body}><strong>Inactivity Policy:</strong> Trading accounts that remain inactive for 30 consecutive calendar days will be automatically archived by the server. An account is considered Inactive if it has not executed at least one trade during the preceding 30-day period.</p>
        <p className={styles.body}>Once an account has been archived due to inactivity, it will be permanently labeled as <strong>Inactive</strong>. Archived accounts are not eligible for refunds, credits, reinstatement, or any other form of compensation under any circumstances.</p>
      </section>
    </>
  );
}

function DFYPlan() {
  return (
    <>
      <p className={styles.effectiveDate}>Effective date: November 30, 2025.</p>
      <p className={styles.intro}>
        Program name: POW Done-For-You Funding ("DFY Program").<br />
        Program operator: POW / AkriTrade, Inc. ("POW", "we", "us").<br />
        Infrastructure provider (B2B Only): Prime Bridge Securities LLC and/or its affiliates ("Prime Bridge" or "PB").
      </p>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>IMPORTANT NOTICE</h2>
        <p className={styles.body}>Contracting Parties. These Terms of Service ("Terms") form a legally binding agreement solely between you (the "User") and POW. Prime Bridge is not a party to this agreement and provides no services directly to Users.</p>
        <p className={styles.body}>Nature of Services. The DFY Program is not an investment product, collective investment scheme, deposit, or savings vehicle. Participation does not constitute an offer of securities or solicitation to invest.</p>
        <p className={styles.body}>No Advice. Neither POW nor Prime Bridge provides investment, trading, financial, legal, or tax advice. You participate entirely at your own risk.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>1. DEFINITIONS</h2>
        <p className={styles.body}>"Account" means a DFY trading account made accessible through POW's platform.</p>
        <p className={styles.body}>"Activation Payment" means the fee paid to POW to access the DFY Program and request a funded allocation.</p>
        <p className={styles.body}>"Performance Share" means any conditional performance-based payout calculated under POW's rules.</p>
        <p className={styles.body}>"PB Services" means brokerage infrastructure, execution routing, liquidity access, margin bridging, and settlement services provided by Prime Bridge exclusively to POW on a B2B basis.</p>
        <p className={styles.body}>"Third-Party Providers" include trading platforms, liquidity providers, custodians, payment processors, and digital asset service providers.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>2. ROLE CLARITY AND LEGAL SEPARATION</h2>
        <p className={styles.body}>2.1 POW as Sole Counterparty. POW is the sole operator of the DFY Program and the only contractual counterparty to Users.</p>
        <p className={styles.body}>2.2 Prime Bridge Boundary. You acknowledge and agree that Prime Bridge:</p>
        <ul className={styles.list}>
          <li className={styles.listItem}>Does not onboard, KYC, contract with, or provide services directly to Users.</li>
          <li className={styles.listItem}>Has no knowledge of, or responsibility for, any agreements, representations, or arrangements between POW and Users or third parties.</li>
          <li className={styles.listItem}>Has no knowledge of, involvement in, or control over trading strategies, signals, or decision-making processes.</li>
          <li className={styles.listItem}>Acts solely as a B2B service provider to POW.</li>
        </ul>
        <p className={styles.body}>2.3 No Agency or Fiduciary Relationship. Nothing in these Terms creates a partnership, agency, joint venture, fiduciary duty, or trust relationship between you and Prime Bridge.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>3. PROGRAM OVERVIEW</h2>
        <p className={styles.body}>3.1 Users pay an Activation Payment to POW to request access to the DFY Program.</p>
        <p className={styles.body}>3.2 POW may utilize PB Services and Third-Party Providers to facilitate execution, liquidity access, margin bridging, and operational settlement.</p>
        <p className={styles.body}>3.3 POW may impose risk controls, trading restrictions, and liquidation mechanisms to protect program integrity.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>4. PAYMENTS, FEES, AND CUSTODY</h2>
        <p className={styles.body}><strong>4.1 Activation Payment.</strong> All Activation Payments are made to POW and are non-refundable unless otherwise required by applicable law. Activation Payments are fees for participation in and access to the DFY Program and do not constitute a deposit, investment, equity contribution, ownership interest, or customer trading capital.</p>
        <p className={styles.body}><strong>4.2 Nature of Funded Accounts.</strong> Accounts made available through the DFY Program are <strong>margin-funded live trading accounts established on TCB LTD’s live MT5 server</strong>. They are not demo or evaluation accounts.</p>
        <p className={styles.body}>The stated funded account value represents the <strong>trading capacity made available to the account subject to the applicable DFY Program risk parameters</strong> and does not represent cash deposited by or held on behalf of the User. For example, a $100,000 funded account is currently subject to a maximum permitted loss of <strong>7.3% of the funded account value</strong>, subject to the applicable Program terms and risk parameters.</p>
        <p className={styles.body}><strong>4.3 Trade Execution.</strong> DFY-funded accounts are assigned to an <strong>A-book group</strong>. Orders generated through such accounts are routed to the live market through Prime Bridge’s external liquidity provider relationships and trading/liquidity accounts. Execution remains subject to market conditions, available liquidity, spreads, slippage, latency, and other execution risks described in these Terms.</p>
        <p className={styles.body}><strong>4.4 Relationship with POW.</strong> Prime Bridge’s commercial relationship in connection with the DFY Program is with <strong>POW and not POW’s underlying customers</strong>. Prime Bridge does not accept customer deposits from, custody assets for, or maintain a direct customer account relationship with POW’s underlying customers. POW is solely responsible for the onboarding, identification, compliance, and customer-facing obligations applicable to its customers.</p>
        <p className={styles.body}>Amounts paid by POW to Prime Bridge and/or Prime Custody in connection with the DFY Program are <strong>activation fees for the provision of funded accounts</strong> and do not constitute deposits or assets held in custody on behalf of POW or its underlying customers. Where eligible realized profits are generated through DFY-funded accounts, <strong>POW is entitled to an 80% profit share</strong>, subject to the applicable payout terms, fees, risk parameters, account status, and other conditions of the DFY Program. POW is responsible for any onward allocation or payment to its underlying customers under its separate agreements with them.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>5. PERFORMANCE SHARE AND WITHDRAWAKS</h2>
        <p className={styles.body}>5.1 Conditional Payouts. Performance Shares are subject to compliance, risk review, and operational constraints.</p>
        <p className={styles.body}>5.2 Payment Methods. POW may process payouts via cryptocurrency or other methods made available in the portal.</p>
        <p className={styles.body}>5.3 Delays and Holds. POW may delay or deny payouts in cases of suspected violations, fraud, abnormal trading, chargebacks, or technical issues.</p>
        <p className={styles.body}>5.4 No PB Liability. Withdrawals paid by Prime Bridge are paid solely to POW pursuant to its B2B arrangements, and Prime Bridge has no knowledge of, involvement in, or responsibility for how POW distributes, allocates, or remits such funds to any Users, contractors, affiliates, or third parties.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>6. RISK MANAGEMENT AND LIQUIDATION</h2>
        <p className={styles.body}>6.1 DFY accounts are subject to a maximum drawdown limit of 7.2% of the starting account balance, as determined and enforced by Prime Bridge as part of its risk controls.</p>
        <p className={styles.body}>6.2 Prime Bridge may liquidate positions or close accounts once the drawdown threshold is reached. To protect the integrity of this limit, liquidation triggers may occur prior to the full 7.2% drawdown level based on market conditions, execution risk, liquidity constraints, or internal risk management thresholds.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>7. PROHIBITED CONDUCT</h2>
        <p className={styles.body}>Users accessing the portal may not engage in:</p>
        <ul className={styles.list}>
          <li className={styles.listItem}>Fraud, misrepresentation, or identity evasion</li>
          <li className={styles.listItem}>Coordinated or abusive trading behavior</li>
          <li className={styles.listItem}>Latency, arbitrage, or toxic flow exploitation</li>
          <li className={styles.listItem}>Unauthorized automation or circumvention of controls</li>
        </ul>
        <p className={styles.body}>Violations may result in immediate termination and forfeiture of payouts.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>8. DISCLAIMERS AND ACKNOWLEDGMENTS</h2>
        <p className={styles.body}>8.1 Trading involves substantial risk and no guarantees of profit.</p>
        <p className={styles.body}>8.2 Platform outages, execution delays, and pricing discrepancies may occur.</p>
        <p className={styles.body}>8.3 Digital asset payouts involve additional risks, including network congestion and custody failures.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>9. DATA AND MONITORING</h2>
        <p className={styles.body}>9.1 POW may monitor trading activity for risk, compliance, and integrity purposes.</p>
        <p className={styles.body}>9.2 Data may be shared with PB only to the extent necessary for PB Services.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>10. NO WARRANTIES</h2>
        <p className={styles.body}>The DFY Program is provided "as is" and "as available" without warranties of any kind.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>11. LIMITATION OF LIABILITY</h2>
        <p className={styles.body}>11.1 Neither POW nor Prime Bridge shall be liable for indirect, incidental, or consequential damages.</p>
        <p className={styles.body}>11.2 POW's aggregate liability shall not exceed the Activation Payment paid for the relevant account.</p>
        <p className={styles.body}>11.3 Prime Bridge's aggregate liability, if any, is capped at USD $100.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>12. RELEASE AND INDEMNIFICATION OF PRIME BRIDGE</h2>
        <p className={styles.body}>12.1 Users fully release Prime Bridge from any claims related to the DFY Program.</p>
        <p className={styles.body}>12.2 Users agree not to bring claims against Prime Bridge.</p>
        <p className={styles.body}>12.3 Prime Bridge is an intended third-party beneficiary of this section.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>13. INDEMNITY</h2>
        <p className={styles.body}>Users agree to indemnify and hold harmless POW and Prime Bridge from claims arising from User conduct or violations.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>14. SUSPENSION AND TERMINATION</h2>
        <p className={styles.body}>POW may suspend or terminate access at its discretion for risk, compliance, or integrity reasons.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>15. REFUND POLICY</h2>
        <p className={styles.body}>Activation Payments are non-refundable unless required by law.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>16. CHANGE TO TERMS</h2>
        <p className={styles.body}>POW may amend these Terms at any time. Continued use constitutes acceptance.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>17. GOVERNING LAW AND DISPUTE RESOLUTION</h2>
        <p className={styles.body}>These Terms are governed by the laws of Saint Vincent and the Grenadines. Any dispute arising out of or in connection with these Terms shall be resolved by binding arbitration seated in Saint Vincent and the Grenadines, conducted in English.</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>18. ACCEPTANCE</h2>
        <p className={styles.body}>By accessing or using the DFY Program, you acknowledge that you have read, understood, and agreed to these Terms.</p>
      </section>
    </>
  );
}

function TermsOfUse() {
  usePageTitle('Terms of Use');
  const [activeTab, setActiveTab] = useState('starter');
  const navigate = useNavigate();

  return (
    <DashboardLayout>
      <div className={styles.page}>
        <button className={styles.closeBtn} onClick={() => navigate(-1)}>
          <X size={20} strokeWidth={2.5} />
        </button>
        <h1 className={styles.pageTitle}>Terms of use</h1>

        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'starter' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('starter')}
          >
            Starter plan
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'dfy' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('dfy')}
          >
            DFY Plan
          </button>
        </div>

        <div className={styles.content}>
          {activeTab === 'starter' ? <StarterPlan /> : <DFYPlan />}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default TermsOfUse;
